import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch } from "lucide-react";
import { api, setPageMeta } from "@/lib/api";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Motion";

const STATUS_COPY = {
  paid: { label: "CONFIRMED", note: "Payment confirmed. Your order is being packed in Dallas and ships within 2-3 business days." },
  pending: { label: "PENDING", note: "Payment has not been confirmed yet. If you just checked out, give it a minute and try again." },
  failed: { label: "FAILED", note: "This payment did not go through. No charge was made — you can reorder from the shop." },
  expired: { label: "EXPIRED", note: "This checkout expired before payment. Nothing was charged." },
};

export default function OrderLookup() {
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [hp, setHp] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPageMeta(
      "Order Lookup | Ninja Run Club",
      "Check the status of your Ninja Run Club order with your email address and NRC order number.",
      { canonicalPath: "/order-lookup" }
    );
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/lookup", { email, order_ref: ref, website: hp });
      setOrder(data);
    } catch {
      setError("No order found for that email and order number. Orders appear here once payment is confirmed — double-check both fields, or email hello@ninjarunclub.com and we will track it down.");
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = order
    ? order.shipped_at
      ? {
          label: "SHIPPED",
          note: `Your order shipped from Dallas${order.carrier ? ` via ${order.carrier}` : ""} on ${new Date(order.shipped_at).toLocaleDateString("en-US", { month: "long", day: "numeric" })}. US standard delivery usually lands in 5-7 business days.`,
        }
      : STATUS_COPY[order.payment_status] || STATUS_COPY.pending
    : null;

  return (
    <div className="bg-night min-h-screen" data-testid="order-lookup-page">
      <div className="px-5 lg:px-10 pt-10">
        <Breadcrumbs light items={[["Home", "/"], ["Shop", "/shop"], ["Order Lookup", "/order-lookup"]]} />
      </div>
      <header className="px-5 lg:px-10 pt-8 pb-10 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">THE SHOP</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">ORDER LOOKUP.</h1>
          <p className="text-white/60 text-sm max-w-2xl mt-5 leading-relaxed">
            Enter the email you used at checkout and your NRC order number — it looks like <span className="font-mono-label text-white/80">NRC-A7K2M9</span> and is shown on your order confirmation screen.
          </p>
        </Reveal>
      </header>

      <div className="px-5 lg:px-10 py-12 max-w-xl">
        <form onSubmit={submit} className="space-y-6" data-testid="order-lookup-form">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div>
            <label htmlFor="order-email" className="font-mono-label text-white/40 block mb-2">EMAIL AT CHECKOUT</label>
            <input
              id="order-email"
              data-testid="order-lookup-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
            />
          </div>
          <div>
            <label htmlFor="order-ref" className="font-mono-label text-white/40 block mb-2">ORDER NUMBER</label>
            <input
              id="order-ref"
              data-testid="order-lookup-ref"
              type="text"
              required
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="NRC-A7K2M9"
              className="w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200 uppercase"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="order-lookup-submit"
            className="btn-invert bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50 w-full sm:w-auto"
          >
            {loading ? "CHECKING..." : "FIND MY ORDER"}
          </button>
        </form>

        {error && (
          <p data-testid="order-lookup-error" className="text-white/60 text-sm mt-8 border border-white/20 p-5 leading-relaxed">{error}</p>
        )}

        {order && (
          <div data-testid="order-lookup-result" className="border border-white/20 p-8 mt-8">
            <div className="flex items-center gap-3">
              <PackageSearch size={22} className="text-white" />
              <p className="font-display text-2xl">ORDER {order.order_ref}</p>
            </div>
            <p data-testid="order-status-badge" className="font-mono-label bg-white text-ink px-3 py-1.5 inline-block mt-4">{statusInfo.label}</p>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{statusInfo.note}</p>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="order-tracking-link"
                className="btn-invert inline-block bg-white text-ink font-mono-label px-5 py-3 border border-white hover:bg-transparent hover:text-white transition-colors duration-200 mt-4"
              >
                TRACK PACKAGE{order.carrier ? ` · ${order.carrier.toUpperCase()}` : ""} →
              </a>
            )}
            <div className="mt-6 space-y-2 text-sm text-white/70">
              <p data-testid="order-product"><span className="text-white/40 font-mono-label">ITEM — </span>{order.product_name}{order.size ? ` · size ${order.size}` : ""}{order.quantity > 1 ? ` × ${order.quantity}` : ""}</p>
              {order.amount != null && (
                <p data-testid="order-total"><span className="text-white/40 font-mono-label">TOTAL — </span>${(order.amount / 100).toFixed(2)} {(order.currency || "usd").toUpperCase()}</p>
              )}
              {order.created_at && (
                <p><span className="text-white/40 font-mono-label">PLACED — </span>{new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              )}
            </div>
            <p className="text-white/40 text-sm mt-6">
              Wrong size or need help? See <Link to="/returns" className="underline underline-offset-4 hover:text-white">returns</Link> or email hello@ninjarunclub.com with your order number.
            </p>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2">
          {[["BACK TO SHOP", "/shop"], ["SHIPPING", "/shipping"], ["RETURNS", "/returns"], ["CONTACT", "/contact"]].map(([label, to]) => (
            <Link key={to} to={to} className="font-mono-label text-white/40 hover:text-white link-underline transition-colors duration-200">{label}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}
